import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getCurrencyForLocale, formatPrice } from "../../../../../../../config/locales";
import { useLocation } from "react-router-dom";
import { showToast } from "../../../../../../../core/common-service/CommonService";
import {
  postUploadAndAnalyzeDocx,
  postCheckAudiobookQuota,
  getUser,
  postGeneratePreviewAudiobook,
  postCheckAudiobookCoupon,
} from "../../../../../../../core/api/Api";
import useLangNavigate from "../../../../../../common/useLangNavigate";
import VoiceSampleModal from "../voice-sample-modal/VoiceSampleModal";
import "./StandaloneAudiobookUpload.css";

const StandaloneAudiobookUpload = () => {
  const { t, i18n } = useTranslation();
  const currency = getCurrencyForLocale(i18n.language);
  const navigate = useLangNavigate();
  const location = useLocation();

  // File state
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [extractedText, setExtractedText] = useState(null);

  // Voice selection state
  const [voiceType, setVoiceType] = useState(null); // 'custom', 'male', 'female'
  const [showVoiceSelection, setShowVoiceSelection] = useState(false);

  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);

  // Payment flow state
  const [showPaymentOption, setShowPaymentOption] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(null); // { discount_percentage, discounted_amount }

  useEffect(() => {
    const stateData = location.state;

    if (stateData && stateData.preloadedFile && stateData.fromBookGeneration) {
      setSelectedFile(stateData.preloadedFile);
      setIsPreloaded(true);
      handlePreloadedFileAnalysis(stateData.preloadedFile);
    }
  }, [location.state]);

  const handlePreloadedFileAnalysis = async (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("docx_file", file);

    try {
      const res = await postUploadAndAnalyzeDocx(formData);
      if (res?.data?.success) {
        const data = res.data.success;
        const characterCount = data.character_count;
        const wordCount = data.word_count;
        const text = data.text;
        const estimatedPrice = (2.5 * (characterCount * 22)) / 100000;
        setFileInfo({
          characterCount: characterCount,
          wordCount: wordCount,
          estimatedPrice: estimatedPrice,
        });
        setExtractedText(text);
        setShowVoiceSelection(true);

        showToast({
          type: "success",
          message: t("standalone_audiobook.file_analyzed"),
        });
      }
    } catch (err) {
      showToast({
        type: "error",
        message:
          err?.response?.data?.error || t("standalone_audiobook.upload_failed"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (
        file.type !==
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
        !file.name.endsWith(".docx")
      ) {
        showToast({
          type: "error",
          message: t("standalone_audiobook.invalid_file_type"),
        });
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showToast({
          type: "error",
          message: t("standalone_audiobook.file_too_large"),
        });
        return;
      }

      setSelectedFile(file);
      setFileInfo(null);
      setExtractedText(null);
      setVoiceType(null);
      setShowVoiceSelection(false);
      setPreviewGenerated(false);
      setPreviewAudioUrl(null);
      setShowPaymentOption(false);
      setIsPreloaded(false);
      setCouponCode("");
      setCouponDiscount(null);
    }
  };

  const handleUploadAndAnalyze = async () => {
    if (!selectedFile) {
      showToast({
        type: "warning",
        message: t("standalone_audiobook.select_file"),
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("docx_file", selectedFile);

    try {
      const res = await postUploadAndAnalyzeDocx(formData);
      if (res?.data?.success) {
        const data = res.data.success;
        const characterCount = data.character_count;
        const wordCount = data.word_count;
        const text = data.text;

        // Calculate price: 2.5 * (characters * 22) / 100000
        const estimatedPrice = (2.5 * (characterCount * 22)) / 100000;

        setFileInfo({
          characterCount: characterCount,
          wordCount: wordCount,
          estimatedPrice: estimatedPrice,
        });
        setExtractedText(text);
        setShowVoiceSelection(true); // Show voice selection after analysis

        showToast({
          type: "success",
          message: t("standalone_audiobook.file_analyzed"),
        });
      }
    } catch (err) {
      showToast({
        type: "error",
        message:
          err?.response?.data?.error || t("standalone_audiobook.upload_failed"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceSelection = (type) => {
    setVoiceType(type);

    if (type === "custom") {
      // Show voice sample modal for custom voice
      document.getElementById("voice-sample-modal").style.display = "block";
    } else {
      // For male/female, directly enable preview button
      // Voice type is stored and will be used when generating preview
    }
  };

  const handleVoiceUploaded = () => {
    // After custom voice is uploaded, user can generate preview
    // Voice is now ready for preview generation
  };

  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      // Get the appropriate voice_id based on selection
      let voiceId = null;

      if (voiceType === "custom") {
        const userRes = await getUser();
        voiceId = userRes?.data?.success?.voice_model_id;

        if (!voiceId) {
          showToast({
            type: "error",
            message: t("standalone_audiobook.voice_model_missing"),
          });
          setPreviewLoading(false);
          return;
        }
      }
      // For male/female, voice_id will be passed as voice_type to backend

      const res = await postGeneratePreviewAudiobook({
        standalone_text: extractedText,
        voice_type: voiceType, // 'custom', 'male', or 'female'
        voice_id: voiceId, // Only for custom voice
      });

      if (res?.data?.success) {
        const audioUrl = res.data.audiobook_url;
        setPreviewAudioUrl(audioUrl);
        setPreviewGenerated(true);
        setShowPaymentOption(true); // Show payment option after preview

        showToast({
          type: "success",
          message: t("standalone_audiobook.preview_generated"),
        });
      }
    } catch (err) {
      showToast({
        type: "error",
        message:
          err?.response?.data?.error ||
          t("standalone_audiobook.preview_failed"),
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCheckCoupon = async () => {
    if (!couponCode.trim()) {
      showToast({ type: "error", message: t("please_enter_coupon") });
      return;
    }

    try {
      const response = await postCheckAudiobookCoupon({
        code: couponCode,
        price: fileInfo?.estimatedPrice,
      });
      if (response?.data?.success) {
        const discount = response.data.success;
        setCouponDiscount(discount);
        showToast({ type: "success", message: t("coupon_valid") });
      }
    } catch (error) {
      setCouponDiscount(null);
      showToast({
        type: "error",
        message: error?.response?.data?.error || t("invalid_coupon"),
      });
    }
  };

  const handleProceedToPayment = async () => {
    try {
      // Check quota
      setLoading(true);
      const quotaRes = await postCheckAudiobookQuota({
        standalone_text: extractedText,
        voice_type: voiceType,
      });

      if (quotaRes?.data?.success) {
        // Navigate to payment page
        navigate("/audiobook-payment", "", {
          state: {
            standalone_text: extractedText,
            price: couponDiscount ? couponDiscount.discounted_amount : fileInfo.estimatedPrice,
            voice_type: voiceType,
            coupon_code: couponDiscount ? couponCode : null,
          },
        });
      }
    } catch (err) {
      showToast({
        type: "error",
        message:
          err?.response?.data?.error ||
          t("standalone_audiobook.quota_check_failed"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileInfo(null);
    setExtractedText(null);
    setVoiceType(null);
    setShowVoiceSelection(false);
    setPreviewGenerated(false);
    setPreviewAudioUrl(null);
    setShowPaymentOption(false);
    setIsPreloaded(false);
    setCouponCode("");
    setCouponDiscount(null);
  };

  return (
    <section className="standalone-audiobook-upload py-100">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="upload-card">
              <h2 className="text-center mb-4">
                {t("standalone_audiobook.title")}
              </h2>
              <p className="text-center text-muted mb-5">
                {t("standalone_audiobook.description")}
              </p>

              <div className="upload-section">
                {!selectedFile ? (
                  <div className="file-input-wrapper">
                    <label htmlFor="docx-upload" className="file-input-label">
                      <div className="upload-icon">📄</div>
                      <p>{t("standalone_audiobook.click_to_upload")}</p>
                      <p className="text-muted small">
                        {t("standalone_audiobook.supported_format")}
                      </p>
                    </label>
                    <input
                      id="docx-upload"
                      type="file"
                      accept=".docx"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </div>
                ) : (
                  <div className="file-selected">
                    <div className="file-info-card">
                      <div className="file-icon">📄</div>
                      <div className="file-details">
                        <h5>{selectedFile.name}</h5>
                        <p className="text-muted">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        className="remove-file-btn"
                        onClick={handleRemoveFile}
                        disabled={loading || previewLoading}
                      >
                        ✕
                      </button>
                    </div>

                    {!fileInfo && !isPreloaded && (
                      <button
                        className="analyze-btn"
                        onClick={handleUploadAndAnalyze}
                        disabled={loading}
                      >
                        {loading
                          ? t("standalone_audiobook.analyzing")
                          : t("standalone_audiobook.analyze_file")}
                      </button>
                    )}

                    {fileInfo && (
                      <div className="analysis-results">
                        <h4>{t("standalone_audiobook.analysis_results")}</h4>
                        <div className="stats-grid">
                          <div className="stat-item">
                            <span className="stat-label">
                              {t("standalone_audiobook.characters")}
                            </span>
                            <span className="stat-value">
                              {fileInfo.characterCount.toLocaleString()}
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">
                              {t("standalone_audiobook.words")}
                            </span>
                            <span className="stat-value">
                              {fileInfo.wordCount.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Voice Selection */}
                        {showVoiceSelection && !voiceType && (
                          <div className="voice-selection-section">
                            <h5 className="text-center mb-3">
                              {t("standalone_audiobook.choose_voice_type")}
                            </h5>
                            <div className="voice-options">
                              <button
                                className="voice-option-btn"
                                onClick={() => handleVoiceSelection("custom")}
                              >
                                <i className="fa-solid fa-microphone"></i>
                                <span>
                                  {t("standalone_audiobook.custom_voice")}
                                </span>
                              </button>
                              <button
                                className="voice-option-btn"
                                onClick={() => handleVoiceSelection("male")}
                              >
                                <i className="fa-solid fa-person"></i>
                                <span>
                                  {t("standalone_audiobook.male_voice")}
                                </span>
                              </button>
                              <button
                                className="voice-option-btn"
                                onClick={() => handleVoiceSelection("female")}
                              >
                                <i className="fa-solid fa-person-dress"></i>
                                <span>
                                  {t("standalone_audiobook.female_voice")}
                                </span>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Selected Voice Display */}
                        {voiceType && (
                          <div className="selected-voice-info">
                            <p className="text-center">
                              <strong>
                                {t("standalone_audiobook.selected_voice")}:
                              </strong>{" "}
                              {voiceType === "custom"
                                ? t("standalone_audiobook.custom_voice")
                                : voiceType === "male"
                                ? t("standalone_audiobook.male_voice")
                                : t("standalone_audiobook.female_voice")}
                            </p>
                          </div>
                        )}

                        {/* Free Trial Button (Essai gratuit) */}
                        {voiceType && !previewGenerated && (
                          <div className="button-group">
                            <button
                              className="free-trial-btn"
                              onClick={generatePreview}
                              disabled={previewLoading}
                            >
                              {previewLoading
                                ? t("standalone_audiobook.generating_preview")
                                : t("standalone_audiobook.free_trial")}
                            </button>
                          </div>
                        )}

                        {/* Preview Audio Player */}
                        {previewGenerated && previewAudioUrl && (
                          <div className="preview-audio-section">
                            <h5 className="text-center mb-3">
                              {t("standalone_audiobook.preview_20_seconds")}
                            </h5>
                            <audio controls className="w-100">
                              <source src={previewAudioUrl} type="audio/mpeg" />
                              {t(
                                "standalone_audiobook.browser_no_audio_support"
                              )}
                            </audio>
                          </div>
                        )}

                        {/* Payment Section (shown after preview) */}
                        {showPaymentOption && (
                          <div className="payment-section">
                            <div className="stat-item highlight">
                              <span className="stat-label">
                                {t("standalone_audiobook.full_audiobook_price")}
                              </span>
                              <span className="stat-value">
                                {formatPrice(fileInfo.estimatedPrice.toFixed(2), i18n.language)}
                              </span>
                            </div>

                            <div className="coupon-input-container">
                              <input
                                className="form-control shadow-none"
                                type="text"
                                placeholder={t("enter_your_coupon")}
                                value={couponCode}
                                onChange={(e) => {
                                  setCouponCode(e.target.value);
                                  setCouponDiscount(null);
                                }}
                              />
                              <button
                                onClick={handleCheckCoupon}
                                type="button"
                                className="aai-btn btn-pill-solid d-flex justify-content-center"
                              >
                                <i className="fa-solid fa-check"></i>
                              </button>
                            </div>

                            {couponDiscount && (
                              <div className="stat-item">
                                <span className="stat-label">
                                  {t("standalone_audiobook.discounted_price")}
                                </span>
                                <span className="stat-value">
                                  {formatPrice(couponDiscount.discounted_amount, i18n.language)}
                                </span>
                              </div>
                            )}

                            <button
                              className="proceed-btn"
                              onClick={handleProceedToPayment}
                              disabled={loading}
                            >
                              {loading
                                ? t("standalone_audiobook.processing")
                                : t("standalone_audiobook.proceed_to_payment")}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <VoiceSampleModal
        id="voice-sample-modal"
        onUploaded={handleVoiceUploaded}
      />
    </section>
  );
};

export default StandaloneAudiobookUpload;
